/**
 * Inject the widget bundle as an SRI-protected <script> and wait for its
 * window global to appear.
 *
 * Why a <script> tag and not `import()`:
 *   `import()` can fetch cross-origin ESM but doesn't honor Subresource
 *   Integrity on the dynamic-import call itself. A <script> tag with
 *   `integrity=…` is the only path where the browser actually refuses
 *   to execute a bundle whose bytes don't match the manifest's hash.
 *
 * The bundle is built with `tsup --format iife --globalName MhosaicFeedback`,
 * so once it parses it sets `window.MhosaicFeedback = { createFeedback }`
 * (per `src/widget.ts`'s exports). We wait for that to appear.
 *
 * Idempotency: if multiple FeedbackProviders mount on the same page (e.g.
 * during a React fast-refresh cycle), we reuse an already-injected bundle
 * instead of fetching a second copy.
 */

import type { FeedbackApi, FeedbackConfig } from '../types'
import type { ErrorTrackingOptions } from '../modules/error-tracking'

/** What the loader may pass to the bundle's `createFeedback`: the public
 *  config plus the bundle-only `errorTracking` knob the loader forwards
 *  from the manifest. */
export type BundleConfig = FeedbackConfig & {
  errorTracking?: boolean | ErrorTrackingOptions
}

declare global {
  interface Window {
    MhosaicFeedback?: {
      createFeedback(config: BundleConfig): FeedbackApi
    }
  }
}

const SCRIPT_DATA_ATTR = 'data-mhosaic-feedback-bundle'

export interface BundleLoadOptions {
  bundleUrl: string
  sriHash: string
  /** Default 30 s. Long enough for slow networks; short enough to surface
   *  CDN outages quickly. */
  timeoutMs?: number
}

export interface BundleHandle {
  createFeedback(config: BundleConfig): FeedbackApi
}

export async function injectBundle(
  opts: BundleLoadOptions,
): Promise<BundleHandle> {
  // Reuse a bundle already loaded this page.
  if (window.MhosaicFeedback?.createFeedback) {
    return window.MhosaicFeedback
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[${SCRIPT_DATA_ATTR}]`,
  )
  if (existing) {
    // Another loader instance started the fetch; wait for the global.
    return waitForGlobal(opts.timeoutMs ?? 30_000)
  }

  return new Promise<BundleHandle>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = opts.bundleUrl
    script.integrity = opts.sriHash
    script.crossOrigin = 'anonymous'
    script.async = true
    script.setAttribute(SCRIPT_DATA_ATTR, '1')
    script.onload = () => {
      if (window.MhosaicFeedback?.createFeedback) {
        resolve(window.MhosaicFeedback)
      } else {
        reject(
          new Error(
            'mhosaic-feedback: bundle loaded but window.MhosaicFeedback.createFeedback is missing — bundle/loader version mismatch?',
          ),
        )
      }
    }
    script.onerror = () => {
      reject(
        new Error(
          `mhosaic-feedback: failed to load bundle from ${opts.bundleUrl} ` +
            '(network, CSP, or SRI mismatch)',
        ),
      )
    }
    document.head.appendChild(script)

    const timeoutMs = opts.timeoutMs ?? 30_000
    setTimeout(() => {
      reject(
        new Error(`mhosaic-feedback: bundle load timeout after ${timeoutMs}ms`),
      )
    }, timeoutMs)
  })
}

function waitForGlobal(timeoutMs: number): Promise<BundleHandle> {
  return new Promise((resolve, reject) => {
    const start = Date.now()
    const tick = () => {
      if (window.MhosaicFeedback?.createFeedback) {
        resolve(window.MhosaicFeedback)
        return
      }
      if (Date.now() - start > timeoutMs) {
        reject(
          new Error(
            'mhosaic-feedback: timed out waiting for bundle global (another loader instance started the fetch but never finished)',
          ),
        )
        return
      }
      setTimeout(tick, 50)
    }
    tick()
  })
}
